import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SITE } from "@/data/content";

const StoryCard = ({ item, testId, tall = false }) => (
  <a
    href={item.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="group relative block h-full overflow-hidden rounded-md border border-line transition-colors duration-300 hover:border-ember/60"
  >
    <img
      src={item.image}
      alt={item.title}
      loading="lazy"
      onError={(e) => {
        if (item.imageFallback && e.currentTarget.src !== item.imageFallback) e.currentTarget.src = item.imageFallback;
      }}
      className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${tall ? "h-[420px] md:h-full md:min-h-[560px]" : "h-64"}`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ember">{item.category}</p>
      <h3 className={`mt-2 font-display font-bold leading-tight text-cream transition-colors duration-300 group-hover:text-emberHover ${tall ? "text-2xl md:text-3xl" : "text-xl"}`}>
        {item.title}
      </h3>
      <p className={`mt-3 text-sm leading-relaxed text-ash ${tall ? "max-w-md" : "line-clamp-2"}`}>
        {item.description}
      </p>
    </div>
  </a>
);

export const GroundReports = () => {
  const { t } = useLang();
  const [featured, ...rest] = t.ground.items;
  return (
    <section id="ground-reports" data-testid="ground-reports-section" className="bg-coal py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          eyebrow={t.ground.eyebrow}
          title={t.ground.title}
          titleItalic={t.ground.titleItalic}
          description={t.ground.description}
        />

        <div className="mt-14 grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7 lg:col-span-8" delay={0.05}>
            <StoryCard item={featured} testId="ground-story-0" tall />
          </Reveal>
          <div className="grid gap-6 md:col-span-5 lg:col-span-4">
            {rest.map((item, i) => (
              <Reveal key={item.title} delay={0.12 + i * 0.08}>
                <StoryCard item={item} testId={`ground-story-${i + 1}`} />
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <a
            href={SITE.youtube}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ground-view-all-link"
            className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ember transition-colors duration-200 hover:text-emberHover"
          >
            {t.ground.readStory}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
